'use client';

import { useTranslation } from 'react-i18next';

import { composeCells } from './identity-cells';
import type {
  DancerExperience,
  PublicDancerProfile,
  ViewContext,
} from './types';

interface Props {
  dancer: PublicDancerProfile;
  /** Resolved page locale (e.g. `'en'`, `'fr'`). Drives ISO code → name
   * translation for nationalities, country, and languages. Defaults to
   * `'en'` when omitted (legacy callers). */
  locale?: string;
  /** Visibility context. When provided, gates registry-controlled cells
   * (appearance, body measurements) per the per-surface registry. When
   * omitted, only ALWAYS cells render — preserves legacy behavior. */
  viewContext?: ViewContext;
  /** All experience rows for the dancer. Used to derive the affiliation
   * sub-line (Company + Rank for pros, School for students). */
  experiences?: DancerExperience[];
}

/**
 * Mono "playbill" identity strip — same shape as the audition page band.
 * Renders one cell per truthy attribute (composed by the shared
 * `composeCells` in `identity-cells.ts`, which the server hero reuses for
 * its docked facts row). Empty strip if everything is null.
 *
 * Cell order (companies-basic-first, per Philippine's 2026-05-11 brief):
 *   Age → Height → Level → Styles → Based in → Languages, then
 *   registry-gated appearance/measurement cells when `viewContext` is set.
 */
export function DancerIdentityStrip({
  dancer,
  locale = 'en',
  viewContext,
  experiences,
}: Props) {
  const { t } = useTranslation();
  const cells = composeCells(dancer, locale, viewContext, experiences, t);
  if (cells.length === 0) return null;

  // Match audition playbill: 1 col mobile / 2 col tablet / N cols desktop.
  // Cap visual columns at 4 so the strip stays scannable when the dancer
  // has many cells; extra cells wrap to a second row at the same width.
  const lgCols =
    cells.length >= 4
      ? 'lg:grid-cols-4'
      : cells.length === 3
        ? 'lg:grid-cols-3'
        : 'lg:grid-cols-2';

  return (
    <section
      aria-label={t('dancer:publicView.identityStrip.ariaLabel')}
      className="border-border border-y"
    >
      <div
        className={`divide-border mx-auto grid max-w-[1320px] grid-cols-1 px-6 sm:grid-cols-2 sm:divide-x sm:px-10 ${lgCols} lg:px-16`}
      >
        {cells.map((cell) => (
          <div key={cell.label} className="px-1 py-5 sm:px-6 sm:py-8">
            <div className="text-muted-foreground/70 font-mono text-[10px] tracking-[0.22em] uppercase">
              {cell.label}
            </div>
            <div className="text-foreground mt-2 text-base leading-snug sm:text-[1.05rem]">
              {cell.value}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
